/**
 * React Query hooks for the Meta read endpoints (A3 §8.1 reads).
 *
 * Mirrors the Google `useCampaignsQuery` shape so the Account page can
 * call both hooks side-by-side and merge the lists into one unified
 * grid. All hooks are gated by `hasLinkedMetaAccounts` from
 * `useAuthStore` — a member with no Meta credential row has nothing to
 * query, so no request is fired and no 4xxs land in the React Query
 * error surface. (Meta features are broadly available now; the old
 * per-UUID beta gate was removed with the free-tier flip.)
 *
 * Cache keys: `['meta-campaigns']`, `['meta-campaign', id]`,
 * `['meta-ad-sets', campaignId]`, `['meta-ads', adSetId]`.
 */
import { useQuery } from '@tanstack/react-query';

import { metaCampaignsApi } from '@/services/api';
import { CACHE_CONFIG } from '@/config/constants';
import { features } from '@/config/features';
import { useAuthStore } from '@/store/authStore';

function getStaleTime(): number {
  const freshness = features.campaignDataFreshness;
  if (freshness === 'refresh_always') return 0;
  if (freshness === 'refresh_on_first_nav') return CACHE_CONFIG.DEFAULT_DURATION;
  return Infinity;
}

export function useMetaCampaignsQuery(campaignStatus?: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasLinkedMetaAccounts = useAuthStore((s) => s.hasLinkedMetaAccounts);
  const enabled = isAuthenticated && hasLinkedMetaAccounts;

  return useQuery({
    queryKey: ['meta-campaigns', campaignStatus ?? null],
    queryFn: () => metaCampaignsApi.getCampaigns(campaignStatus),
    enabled,
    staleTime: getStaleTime(),
    gcTime: CACHE_CONFIG.DEFAULT_DURATION * 2,
    retry: 2,
  });
}

export function useMetaCampaignQuery(campaignId: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasLinkedMetaAccounts = useAuthStore((s) => s.hasLinkedMetaAccounts);
  const enabled =
    isAuthenticated &&
    !!campaignId &&
    hasLinkedMetaAccounts;

  return useQuery({
    queryKey: ['meta-campaign', campaignId],
    queryFn: () => metaCampaignsApi.getCampaign(campaignId),
    enabled,
    staleTime: getStaleTime(),
    gcTime: CACHE_CONFIG.DEFAULT_DURATION * 2,
    retry: 2,
  });
}

export function useMetaAdSetsQuery(campaignId: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasLinkedMetaAccounts = useAuthStore((s) => s.hasLinkedMetaAccounts);
  const enabled =
    isAuthenticated &&
    !!campaignId &&
    hasLinkedMetaAccounts;

  return useQuery({
    queryKey: ['meta-ad-sets', campaignId],
    queryFn: () => metaCampaignsApi.getAdSets(campaignId),
    enabled,
    staleTime: getStaleTime(),
    gcTime: CACHE_CONFIG.DEFAULT_DURATION * 2,
    retry: 2,
  });
}

export function useMetaAdsQuery(adSetId: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasLinkedMetaAccounts = useAuthStore((s) => s.hasLinkedMetaAccounts);
  const enabled =
    isAuthenticated &&
    !!adSetId &&
    hasLinkedMetaAccounts;

  return useQuery({
    queryKey: ['meta-ads', adSetId],
    queryFn: () => metaCampaignsApi.getAds(adSetId),
    enabled,
    staleTime: getStaleTime(),
    gcTime: CACHE_CONFIG.DEFAULT_DURATION * 2,
    retry: 2,
  });
}
